import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { RouteSeo } from "@/components/RouteSeo";
import { ScrollToTop } from "@/components/shared/ScrollToTop";
import { PageTransition } from "@/components/shared/PageTransition";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { OfflineBanner } from "@/components/layout/OfflineBanner";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { NotificationPermissionPrompt } from "@/components/notifications/NotificationPermissionPrompt";
import { CookieConsentBanner } from "@/components/layout/CookieConsentBanner";
import { LeadCapturePopup } from "@/components/layout/LeadCapturePopup";
import { startAdTracking } from "@/lib/adPixels";
import { trackPageView } from "@/lib/pageTracking";
import { CartProvider } from "@/contexts/CartContext";

import { usePWAInstall } from "@/hooks/usePWAInstall";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { LiveChat } from "@/components/chat/LiveChat";
import { AiAssistant } from "@/components/chat/AiAssistant";
import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { toast } from "@/hooks/use-toast";

// Pages
import Home from "./pages/Home";
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";
import RoleSelection from "./pages/RoleSelection";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import SellerDashboard from "./pages/seller/Dashboard";
import AddProduct from "./pages/seller/AddProduct";
import EditProduct from "./pages/seller/EditProduct";
import ProductList from "./pages/seller/ProductList";
import BankAccounts from "./pages/seller/BankAccounts";
import SellerOrders from "./pages/seller/Orders";
import AdminDashboard from "./pages/admin/AdminDashboard";
import BuyerOrders from "./pages/buyer/Orders";
import BuyerBankAccounts from "./pages/buyer/BankAccounts";
import Payment from "./pages/Payment";
import PaymentSuccess from "./pages/PaymentSuccess";
import NotFound from "./pages/NotFound";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import HelpCenter from "./pages/HelpCenter";
import Contact from "./pages/Contact";
import ShippingInfo from "./pages/ShippingInfo";
import Returns from "./pages/Returns";
import RefundPolicy from "./pages/RefundPolicy";
import TrackOrder from "./pages/TrackOrder";
import Categories from "./pages/Categories";
import Sellers from "./pages/Sellers";
import WhatsAppSettings from "./pages/seller/WhatsAppSettings";
import Catalog from "./pages/seller/Catalog";
import ShareProduct from "./pages/ShareProduct";
import CheckoutFlow from "./pages/CheckoutFlow";
import DeliveryInfoForm from "./components/buyer/DeliveryInfoForm";
import Cart from "./pages/Cart";
import HowToUse from "./pages/HowToUse";
import Profile from "./pages/Profile";
import ConfirmDelivery from "./pages/ConfirmDelivery";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import OrderPlaced from "./pages/OrderPlaced";

const queryClient = new QueryClient();

// Records a page view on every route change (first-party web traffic analytics)
const PageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    void trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

const AppContent = () => {
  
  // PWA install prompt
  const { showInstallPrompt, handleInstallClick, dismissPrompt } = usePWAInstall();
  
  // Push notifications
  const { permission, requestPermission } = usePushNotifications();
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  
  // Auth for chat
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Consent-gated ad & measurement tags (Meta, TikTok)
  useEffect(() => {
    void startAdTracking();
  }, []);


  // Global unhandled rejection handler to prevent blank pages
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled promise rejection:", event.reason);
      // Don't show toast for network errors during offline mode
      if (!navigator.onLine) return;
      
      toast({
        title: "An error occurred",
        description: "Please try again or refresh the page.",
        variant: "destructive",
      });
      // Prevent the error from crashing the app
      event.preventDefault();
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    return () => window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  }, []);

  // Show notification prompt after install or on first visit
  useEffect(() => {
    const hasSeenNotificationPrompt = localStorage.getItem('notification-prompt-seen');
    if (!hasSeenNotificationPrompt && permission === 'default') {
      // Show notification prompt 45 seconds after page load
      const timer = setTimeout(() => {
        setShowNotificationPrompt(true);
      }, 45000);
      return () => clearTimeout(timer);
    }
  }, [permission]);

  const handleAllowNotifications = async () => {
    await requestPermission();
    setShowNotificationPrompt(false);
    localStorage.setItem('notification-prompt-seen', 'true');
  };

  const handleDismissNotifications = () => {
    setShowNotificationPrompt(false);
    localStorage.setItem('notification-prompt-seen', 'true');
  };

  return (
    <BrowserRouter>
      <ScrollToTop />
      <PageTracker />
      <div className="min-h-screen flex flex-col">
        <Header />
        <OfflineBanner />
        
        {/* PWA Install Prompt */}
        {showInstallPrompt && (
          <InstallPrompt 
            onInstall={handleInstallClick} 
            onDismiss={dismissPrompt} 
          />
        )}
        
        {/* Notification Permission Prompt */}
        {showNotificationPrompt && (
          <NotificationPermissionPrompt
            onAllow={handleAllowNotifications}
            onDismiss={handleDismissNotifications}
          />
        )}
        
        <main className="flex-1">
          <PageTransition>
            <RouteSeo />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/role-selection" element={<RoleSelection />} />
              <Route path="/login" element={<Auth />} />
              <Route path="/register" element={<Auth />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/:productId" element={<ProductDetail />} />
              <Route path="/seller/dashboard" element={<SellerDashboard />} />
              <Route path="/seller/add-product" element={<AddProduct />} />
              <Route path="/seller/edit-product/:id" element={<EditProduct />} />
              <Route path="/seller/products" element={<ProductList />} />
              <Route path="/seller/orders" element={<SellerOrders />} />
              <Route path="/seller/bank-accounts" element={<BankAccounts />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/buyer/orders" element={<BuyerOrders />} />
              <Route path="/buyer/bank-accounts" element={<BuyerBankAccounts />} />
              <Route path="/payment" element={<Payment />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/order-placed" element={<OrderPlaced />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/help-center" element={<HelpCenter />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/shipping-info" element={<ShippingInfo />} />
              <Route path="/returns" element={<Returns />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/track-order" element={<TrackOrder />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/sellers" element={<Sellers />} />
              <Route path="/seller/whatsapp-settings" element={<WhatsAppSettings />} />
              <Route path="/seller/catalog" element={<Catalog />} />
              <Route path="/share/:productId" element={<ShareProduct />} />
              <Route path="/share/product/:productId" element={<ShareProduct />} />
              <Route path="/checkout" element={<CheckoutFlow />} />
              <Route path="/checkout-flow" element={<CheckoutFlow />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/delivery-info" element={<DeliveryInfoForm />} />
              <Route path="/how-to-use" element={<HowToUse />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/buyer/profile" element={<Profile />} />
              <Route path="/seller/profile" element={<Profile />} />
              <Route path="/confirm-delivery" element={<ConfirmDelivery />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </PageTransition>
        </main>
            <Footer />
            <CookieConsentBanner />
            <LeadCapturePopup />
            
            {/* Live Chat Widget - only for non-admin users */}
            {user && !isAdmin && <LiveChat />}
            {/* AI Assistant Widget - only for non-admin users */}
            {user && !isAdmin && <AiAssistant />}
          </div>
        </BrowserRouter>
      );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <CartProvider>
      <TooltipProvider>
        <ErrorBoundary>
          <Toaster />
          <Sonner />
          <AppContent />
        </ErrorBoundary>
      </TooltipProvider>
    </CartProvider>
  </QueryClientProvider>
);

export default App;
